import {decorate, inject, injectable} from '@yosmanyga/di';
import {MapApi, MapResolutionType, MapRouteType} from '../data';
import {action, observable, runInAction, makeObservable} from 'mobx';

class MapController {
    loaded: boolean;
    map: MapResolutionType;
    route: MapRouteType | null;

    constructor(private mapApi: MapApi) {
        this.loaded = false;
        this.map = {
            places: [],
            matters: [],
            placePayloads: [],
            matterPayloads: [],
        };
        this.route = null;
        
        makeObservable(this, {
            loaded: observable,
            map: observable,
            route: observable,
            load: action,
            setPlacePayload: action,
            setMatterPayload: action,
            resolveRoute: action,
            clearRoute: action,
            isFirst: action,
        });
    }

    load = async () => {
        const map = await this.mapApi.resolve();

        runInAction(() => {
            this.map = map;
            this.loaded = true;
        });
    };

    setPlacePayload = async (definition: string, expanded: boolean) => {
        let found = false;

        runInAction(() => {
            this.map = {
                ...this.map,
                placePayloads: this.map.placePayloads.map((placePayload) => {
                    if (placePayload.definition !== definition) {
                        return placePayload;
                    }

                    found = true;

                    return {
                        ...placePayload,
                        expanded: expanded,
                    };
                }),
            };
        });

        if (!found) {
            runInAction(() => {
                this.map = {
                    ...this.map,
                    placePayloads: [
                        ...this.map.placePayloads,
                        {
                            definition: definition,
                            expanded: expanded,
                        },
                    ],
                };
            });
        }

        this.mapApi.setPlacePayload(definition, expanded).then();
    };

    setMatterPayload = async (definition: string, icon: string) => {
        let found = false;

        runInAction(() => {
            this.map = {
                ...this.map,
                matterPayloads: this.map.matterPayloads.map((matterPayload) => {
                    if (matterPayload.definition !== definition) {
                        return matterPayload;
                    }

                    found = true;

                    return {
                        ...matterPayload,
                        icon: icon,
                    };
                }),
            };
        });

        if (!found) {
            runInAction(() => {
                this.map = {
                    ...this.map,
                    matterPayloads: [
                        ...this.map.matterPayloads,
                        {
                            definition: definition,
                            icon: icon,
                        },
                    ],
                };
            });
        }

        this.mapApi.setMatterPayload(definition, icon).then();
    };

    resolveRoute = async (place: string) => {
        const route = await this.mapApi.resolveRoute(place);

        runInAction(() => {
            this.route = route;
        });
    };

    clearRoute = () => {
        runInAction(() => {
            this.route = null;
        });
    };

    isFirst = (definition: string) => {
        if (!this.route) {
            return false
        }

        return Object.values(this.route)[0].definition === definition
    }
}

decorate(injectable(), MapController);
decorate(inject(MapApi), MapController, 0);

export {MapController};
